import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { departmentAPI, adminAPI } from "../services/api";
import { toast } from "sonner";
import {
  Building2,
  Search,
  Plus,
  Edit,
  X,
  Save,
  Mail,
  Phone,
  Loader2,
} from "lucide-react";
import { motion } from "framer-motion";

interface Department {
  id: string;
  name: string;
  description: string | null;
  managerId: string | null;
  email: string | null;
  phoneNumber: string | null;
  isActive: boolean;
  createdAt: string;
}

export default function DepartmentManagement() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [selectedDept, setSelectedDept] = useState<Department | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [managers, setManagers] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    email: "",
    phoneNumber: "",
    managerId: "",
  });

  useEffect(() => {
    loadDepartments();
    const userStr = localStorage.getItem("campus-itsm-user");
    const user = userStr ? JSON.parse(userStr) : null;
    if (user && user.role === "admin") {
      loadManagers();
    }
  }, []);

  const loadManagers = async () => {
    try {
      const response = await adminAPI.technicianStats();
      if (response.success && response.data) {
        setManagers(response.data);
      } else if (Array.isArray(response)) {
        setManagers(response);
      } else {
        setManagers([]);
      }
    } catch (error) {
      setManagers([]);
    }
  };

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const response = await departmentAPI.list();

      if (response.success || Array.isArray(response)) {
        const deptsList = Array.isArray(response)
          ? response
          : response.data?.departments ||
            response.data?.items ||
            response.data ||
            [];
        setDepartments(Array.isArray(deptsList) ? deptsList : []);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      toast.error("Failed to load departments");
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedDept(null);
    setFormData({
      name: "",
      description: "",
      email: "",
      phoneNumber: "",
      managerId: "",
    });
    setShowDialog(true);
  };

  const openEditDialog = (dept: Department) => {
    setSelectedDept(dept);
    setFormData({
      name: dept.name,
      description: dept.description || "",
      email: dept.email || "",
      phoneNumber: dept.phoneNumber || "",
      managerId: dept.managerId || "",
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || formData.name.trim().length < 2) {
      toast.error("Department name must be at least 2 characters");
      return;
    }

    setIsSubmitting(true);

    try {
      if (selectedDept) {
        const response = await departmentAPI.update(selectedDept.id, {
          name: formData.name,
          description: formData.description || null,
          email: formData.email || null,
          phoneNumber: formData.phoneNumber || null,
        });

        if (response.success) {
          toast.success("Department updated successfully");
          loadDepartments();
          setShowDialog(false);
        }
      } else {
        let managerId = formData.managerId;
        if (!managerId) {
          const userStr = localStorage.getItem("campus-itsm-user");
          const user = userStr ? JSON.parse(userStr) : null;

          if (user) {
            managerId = user.id || user.userId || user.ID || user.user_id;
          }

          if (!managerId) {
            toast.error(
              "Unable to determine manager. Please try logging in again.",
            );
            setIsSubmitting(false);
            return;
          }
        }

        const payload: Record<string, any> = {
          name: formData.name.trim(),
        };

        if (managerId) {
          payload.managerId = managerId;
        }

        const description = formData.description?.trim();
        if (description) {
          payload.description = description;
        }

        const email = formData.email?.trim();
        if (email) {
          payload.email = email;
        }

        const phoneNumber = formData.phoneNumber?.trim();
        if (phoneNumber) {
          payload.phoneNumber = phoneNumber;
        }

        const response = await departmentAPI.create(payload);

        if (response.success) {
          toast.success("Department created successfully");
          loadDepartments();
          setShowDialog(false);
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to save department");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredDepartments = Array.isArray(departments)
    ? departments.filter((dept) =>
        (dept.name || "").toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : [];

  const stats = {
    total: Array.isArray(departments) ? departments.length : 0,
    active: Array.isArray(departments)
      ? departments.filter((d) => d.isActive).length
      : 0,
  };

  return (
    <DashboardLayout>
      <div className="min-h-full p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
              Department Management
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage organizational departments and structure
            </p>
          </motion.div>

          <button
            onClick={openCreateDialog}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all hover:scale-105 shadow-lg cursor-pointer"
          >
            <Plus className="w-5 h-5" />
            Add Department
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {[
            {
              id: "total",
              label: "Total Departments",
              value: stats.total,
              color: "text-primary",
            },
            {
              id: "active",
              label: "Active",
              value: stats.active,
              color: "text-green-400",
            },
          ].map((stat, idx) => (
            <motion.div
              key={stat.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-card border border-border/50 rounded-xl p-4 shadow-lg"
            >
              <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.color} font-mono`}>
                {stat.value}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/50 rounded-xl p-4 shadow-lg"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="search"
              placeholder="Search departments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
            />
          </div>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredDepartments.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No departments found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDepartments.map((dept, idx) => (
              <motion.div
                key={dept.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-card border border-border/50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground">{dept.name}</h3>
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          dept.isActive
                            ? "bg-green-500/10 text-green-400"
                            : "bg-gray-500/10 text-gray-400"
                        }`}
                      >
                        {dept.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>

                {dept.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {dept.description}
                  </p>
                )}

                <div className="space-y-2 mb-4">
                  {dept.email && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Mail className="w-3 h-3" />
                      <span className="truncate">{dept.email}</span>
                    </div>
                  )}
                  {dept.phoneNumber && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Phone className="w-3 h-3" />
                      <span>{dept.phoneNumber}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-4 border-t border-border/30">
                  <button
                    onClick={() => openEditDialog(dept)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg transition-colors text-sm font-medium cursor-pointer"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {showDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card border border-border/50 rounded-2xl p-6 max-w-lg w-full shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-foreground">
                  {selectedDept ? "Edit Department" : "Create New Department"}
                </h3>
                <button
                  onClick={() => setShowDialog(false)}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Department Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        name: e.target.value,
                      })
                    }
                    required
                    minLength={2}
                    placeholder="Enter department name (min. 2 characters)"
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground resize-none"
                  />
                </div>

                {managers.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Department Manager (Optional)
                    </label>
                    <select
                      value={formData.managerId}
                      onChange={(e) =>
                        setFormData({ ...formData, managerId: e.target.value })
                      }
                      className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                    >
                      <option value="">Use current user as manager</option>
                      {managers.map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {manager.fullName || manager.name} ({manager.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, phoneNumber: e.target.value })
                    }
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowDialog(false)}
                    className="flex-1 px-4 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all font-medium cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all disabled:opacity-50 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        {selectedDept ? "Update" : "Create"} Department
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
